/**
 * career-heartbeat — GATE-3's push-plane probe surface (dev only). A trigger
 * button POSTs the api's dev.heartbeat through the BFF proxy; on the fast-return
 * ids it subscribes the push channel to the run's stream, then the rail renders
 * the searchRun store projection LIVE (aria-live) as events arrive over the ONE
 * apply path — replay for events already landed, live ticks for later ones.
 */

import {
  KitElement,
  signal,
  type ActionMap,
  type KitContext,
  type PushChannel,
  type ReadonlySignal,
  type Signal,
  type ViewModel,
} from "@platform/web-kit"
import { classMap, html, nothing, repeat, type TemplateResult } from "@platform/web-kit/render"
import { I18nToken, PushToken, SearchRunStoreToken, apiPost } from "../services"
import type { CareerI18n, CareerKey } from "../../i18n"
import type { SearchRunStage, SearchRunStatus, SearchRunStore, SearchRunView } from "../domain"

const STAGE_KEY: Record<SearchRunStage, CareerKey> = {
  discovering: "heartbeat.stage.discovering",
  verifying: "heartbeat.stage.verifying",
  matching: "heartbeat.stage.matching",
  enriching: "heartbeat.stage.enriching",
  done: "heartbeat.stage.done",
}
const STATUS_KEY: Record<SearchRunStatus, CareerKey> = {
  running: "heartbeat.status.running",
  completed: "heartbeat.status.completed",
  stopped: "heartbeat.status.stopped",
  failed: "heartbeat.status.failed",
}

interface HeartbeatResponse {
  searchId: string
  streamId: string
  appended: number
}

export class HeartbeatVm implements ViewModel {
  readonly runs: ReadonlySignal<readonly SearchRunView[]>
  readonly triggering: Signal<boolean> = signal(false)
  readonly error: Signal<string | null> = signal<string | null>(null)
  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>>
  readonly t: CareerI18n["t"]
  /** Every run stream opened this session (subscribe REPLACES the set). */
  private readonly streams = new Set<string>()

  constructor(store: SearchRunStore, private readonly push: PushChannel, i18n: CareerI18n) {
    this.runs = store.list("aktiv")
    this.t = i18n.t
    this.renderDeps = [this.runs, this.triggering, this.error]
  }

  async trigger(): Promise<void> {
    this.triggering.set(true)
    this.error.set(null)
    try {
      const { streamId } = await apiPost<HeartbeatResponse>("/api/dev/heartbeat")
      this.streams.add(streamId)
      // Subscribe to every opened stream — replay lands the already-appended
      // events, live frames tick in over the same connection as the run runs.
      this.push.subscribe([...this.streams])
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.triggering.set(false)
    }
  }
}

export class CareerHeartbeat extends KitElement<HeartbeatVm> {
  protected createViewModel(ctx: KitContext): HeartbeatVm {
    return new HeartbeatVm(ctx.inject(SearchRunStoreToken), ctx.inject(PushToken), ctx.inject(I18nToken))
  }

  protected render(vm: HeartbeatVm): TemplateResult {
    const runs = vm.runs.value
    const busy = vm.triggering.value
    const err = vm.error.value
    return html`
      <section class="heartbeat">
        <h1 class="surface-title" lang="de">${vm.t("heartbeat.title")}</h1>
        <p class="heartbeat-intro" lang="de">${vm.t("heartbeat.intro")}</p>
        <button
          class=${classMap({ "heartbeat-trigger": true, "is-pending": busy })}
          data-act="trigger-heartbeat"
          ?disabled=${busy}
          lang="de"
        >
          ${busy ? vm.t("heartbeat.triggering") : vm.t("heartbeat.trigger")}
        </button>
        ${err ? html`<p class="heartbeat-error" lang="de">${vm.t("heartbeat.error", { message: err })}</p>` : nothing}
        <h2 class="heartbeat-rail-title" lang="de">${vm.t("heartbeat.railTitle")}</h2>
        <ol class="heartbeat-rail" role="log" aria-live="polite" aria-label="Suchläufe">
          ${runs.length === 0
            ? html`<li class="heartbeat-empty" lang="de">${vm.t("heartbeat.empty")}</li>`
            : repeat(
                runs,
                (run) => run.urn,
                (run) => html`
                  <li class=${classMap({ "heartbeat-run": true, [`is-${run.status}`]: true })} data-run-id=${run.urn}>
                    <span class="run-stage chip" lang="de">${vm.t(STAGE_KEY[run.stage])}</span>
                    <span class="run-status" lang="de">${vm.t(STATUS_KEY[run.status])}</span>
                    <span class="run-count chip" lang="de">${vm.t("heartbeat.count.discovered", { n: run.discovered })}</span>
                    <span class="run-count chip" lang="de">${vm.t("heartbeat.count.verified", { n: run.verified })}</span>
                    <span class="run-count chip" lang="de">${vm.t("heartbeat.count.matched", { n: run.matched })}</span>
                  </li>
                `,
              )}
        </ol>
      </section>
    `
  }

  protected override actions(vm: HeartbeatVm): ActionMap {
    return { "trigger-heartbeat": () => void vm.trigger() }
  }
}
